/**
 * Simulator Results Controller (EPIC-026.5)
 *
 * Renders the right-column blocks from a `SimulationResult` payload returned
 * by `POST /simulator/calculate` (EPIC-026.3):
 *  - 4 KPIs (CA HT, URSSAF, IR, revenu net)
 *  - CA breakdown per fiscal category
 *  - Native SVG graph (delegated to `svg-graph` controller)
 *  - Timeline of milestones
 *  - ACRE impact card (toggled by `acreEligible`)
 *  - ARE vs ARCE comparison (toggled by allocation)
 *  - CFE + insurance row (derived from form inputs — D5.4)
 *  - TJM block (toggled by activity category)
 *  - Dynamic warning banners
 *
 * Uses `cloneTemplate()` + `fillSlots()` exclusively — no inline HTML strings.
 *
 * EPIC-068.4 — end-card de conversion en bas de page : révélée uniquement pour
 * un compte prospect authentifié ; le CTA primaire est adapté selon
 * `organization.prospectHasExistingMicro` (clone d'un des deux templates).
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

// Statuts d'organisation considérés comme « prospect » (cf. OrgStatus PHP).
const PROSPECT_STATUSES = new Set(["prospect", "prospect_inactive"]);

export default class SimulatorResultsController extends BaseController {
  static targets = [
    "kpis",
    "caBreakdown",
    "graphMount",
    "timelineList",
    "acreCard",
    "acreSavings",
    "acreWithout",
    "acreWith",
    "allocationCard",
    "cfeInsuranceRow",
    "tjmCard",
    "tjmMin",
    "tjmTarget",
    "tjmOptimal",
    "warnings",
    "endcard",
    "endcardCta",
  ];

  connect() {
    // No initial paint — the form controller calls `render()` on first calculate.
    this._renderEndcard();
  }

  // ---------------------------------------------------------------------------
  // End-card de conversion (EPIC-068.4) — indépendante du résultat de calcul.
  //   Affichée seulement pour un compte prospect authentifié.
  //   prospectHasExistingMicro === true  → CTA activation prioritaire.
  //   prospectHasExistingMicro === false / null → CTA création prioritaire.
  // Échec /auth/me ou compte non prospect → l'end-card reste masquée.
  // ---------------------------------------------------------------------------

  async _renderEndcard() {
    if (!this.hasEndcardTarget || !this.hasEndcardCtaTarget) return;
    let ctx;
    try {
      ctx = await api.getMeCached();
    } catch (_err) {
      // Non authentifié / erreur → l'end-card reste masquée (dégradation OK).
      return;
    }
    const org = ctx?.organization ?? null;
    if (org === null || !PROSPECT_STATUSES.has(org.status)) return;

    const hasMe = org.prospectHasExistingMicro === true;
    const templateId = hasMe ? "tpl-endcard-cta-has-me" : "tpl-endcard-cta-no-me";
    this.endcardCtaTarget.replaceChildren(this.cloneTemplate(templateId));
    this.endcardTarget.hidden = false;
  }

  render(payload, formState) {
    if (!payload || !payload.data) return;
    const data = payload.data;

    this.renderKpis(data.kpis);
    this.renderCaBreakdown(data.totals, formState);
    this.renderTimeline(data.timeline);
    this.renderAcre(data, formState);
    this.renderAllocation(data.allocationComparison, formState);
    this.renderCfeInsurance(formState);
    this.renderTjm(data.tjm, formState);
    this.renderWarnings(data, formState);
    this.renderGraph(data.monthlyProjection, formState);
  }

  renderKpis(kpis) {
    if (!this.hasKpisTarget || !Array.isArray(kpis)) return;
    const root = this.kpisTarget;
    while (root.firstChild) root.removeChild(root.firstChild);
    kpis.slice(0, 4).forEach((kpi) => {
      const fragment = this.cloneTemplate("tpl-kpi");
      this.fillSlots(fragment, {
        label: kpi.label || "",
        value: formatNumber(kpi.value),
        unit: kpi.unit || "€",
        detail: kpi.detail || "",
      });
      const detailEl = fragment.querySelector('[data-slot="detail"]');
      if (detailEl && kpi.tone) {
        detailEl.classList.add(kpi.tone === "success" ? "is-success" : "");
      }
      root.appendChild(fragment);
    });
  }

  renderCaBreakdown(totals, formState) {
    if (!this.hasCaBreakdownTarget) return;
    const root = this.caBreakdownTarget;
    while (root.firstChild) root.removeChild(root.firstChild);

    const months = formState.simulationMonths || 12;
    const labels = {
      bic_vente: "BIC vente de marchandises",
      bic_services: "BIC prestations de services",
      bnc: "BNC professions libérales",
    };
    const sumsByRegime = { bic_vente: 0, bic_services: 0, bnc: 0 };
    (formState.activities || []).forEach((a) => {
      if (sumsByRegime[a.regime] !== undefined) {
        sumsByRegime[a.regime] += (a.monthlyCa || 0) * months;
      }
    });

    let total = 0;
    Object.entries(labels).forEach(([key, label]) => {
      const v = sumsByRegime[key];
      const fragment = this.cloneTemplate("tpl-ca-breakdown-row");
      this.fillSlots(fragment, {
        label,
        value: v > 0 ? `${formatNumber(v)} €` : "—",
      });
      const valueEl = fragment.querySelector('[data-slot="value"]');
      if (valueEl && v === 0) valueEl.classList.add("text-subtle");
      root.appendChild(fragment);
      total += v;
    });

    const totalFragment = this.cloneTemplate("tpl-ca-breakdown-total");
    this.fillSlots(totalFragment, {
      value: `${formatNumber(total)} €`,
    });
    root.appendChild(totalFragment);
  }

  renderTimeline(timeline) {
    if (!this.hasTimelineListTarget) return;
    const root = this.timelineListTarget;
    while (root.firstChild) root.removeChild(root.firstChild);

    if (!Array.isArray(timeline) || timeline.length === 0) {
      const empty = this.cloneTemplate("tpl-timeline-empty");
      root.appendChild(empty);
      return;
    }

    timeline.forEach((m) => {
      const fragment = this.cloneTemplate("tpl-timeline-row");
      this.fillSlots(fragment, {
        when: formatTimelineDate(m.date),
        what: m.title || "",
        who: m.detail || "",
      });
      root.appendChild(fragment);
    });
  }

  renderAcre(data, formState) {
    if (!this.hasAcreCardTarget) return;
    const visible = Boolean(formState.acreEligible);
    this.acreCardTarget.hidden = !visible;
    if (!visible) return;

    const savings = pickTotal(data.totals, "acreSavings", 0);
    const withoutAcre = pickTotal(data.totals, "urssafWithoutAcre", 0);
    const withAcre = pickTotal(data.totals, "urssafWithAcre", 0);

    if (this.hasAcreSavingsTarget) {
      this.acreSavingsTarget.textContent = `− ${formatNumber(savings)} €`;
    }
    if (this.hasAcreWithoutTarget) {
      this.acreWithoutTarget.textContent = `${formatNumber(withoutAcre)} €`;
    }
    if (this.hasAcreWithTarget) {
      this.acreWithTarget.textContent = `${formatNumber(withAcre)} €`;
    }
  }

  renderAllocation(comparison, formState) {
    if (!this.hasAllocationCardTarget) return;
    const visible = formState.allocation === "are" || formState.allocation === "arce";
    this.allocationCardTarget.hidden = !visible;
    if (!visible) return;

    const are = (comparison && comparison.are) || {};
    const arce = (comparison && comparison.arce) || {};
    const root = this.allocationCardTarget;
    setText(root, '[data-slot="are-net-cumul"]', `${formatNumber(are.netCumul || 0)} €`);
    setText(root, '[data-slot="are-total"]', `+ ${formatNumber(are.totalReceived || 0)} €`);
    setText(root, '[data-slot="are-exhaustion"]', are.exhaustionLabel || "—");
    setText(root, '[data-slot="arce-net-cumul"]', `${formatNumber(arce.netCumul || 0)} €`);
    setText(root, '[data-slot="arce-total"]', `+ ${formatNumber(arce.totalReceived || 0)} €`);
    setText(root, '[data-slot="arce-tranches"]', arce.tranchesLabel || "M0 + M6");
  }

  renderCfeInsurance(formState) {
    if (!this.hasCfeInsuranceRowTarget) return;
    const months = formState.simulationMonths || 12;
    const root = this.cfeInsuranceRowTarget;

    const cfeYear0 = 0;
    const cfeYearN = formState.cfeAmount || 0;
    setText(root, '[data-slot="cfe-year-0"]', `0 €`);
    setText(root, '[data-slot="cfe-year-n"]', `${formatNumber(cfeYearN)} €`);

    const rcPro = formState.rcProAnnual || 0;
    const mutuelle = (formState.mutuelleMonthly || 0) * 12;
    const prevoyance = (formState.prevoyanceMonthly || 0) * 12;
    const totalAssurances =
      (rcPro + mutuelle + prevoyance) * (months / 12);

    setText(root, '[data-slot="rc-pro-year"]', `${formatNumber(rcPro)} €/an`);
    setText(root, '[data-slot="mutuelle-year"]', `${formatNumber(mutuelle)} €/an`);
    setText(root, '[data-slot="prevoyance-year"]', `${formatNumber(prevoyance)} €/an`);
    setText(
      root,
      '[data-slot="assurances-total"]',
      `Total sur ${months} mois : ${formatNumber(totalAssurances)} €`,
    );
  }

  renderTjm(tjm, formState) {
    if (!this.hasTjmCardTarget) return;
    const hasServicesOrBnc = (formState.activities || []).some(
      (a) => a.regime === "bic_services" || a.regime === "bnc",
    );
    this.tjmCardTarget.hidden = !hasServicesOrBnc;
    if (!hasServicesOrBnc) return;

    const row = tjm && tjm[0] ? tjm[0] : { minDaily: 0, targetDaily: 0, optimalDaily: 0 };
    if (this.hasTjmMinTarget) this.tjmMinTarget.textContent = formatNumber(row.minDaily || 0);
    if (this.hasTjmTargetTarget) this.tjmTargetTarget.textContent = formatNumber(row.targetDaily || 0);
    if (this.hasTjmOptimalTarget) this.tjmOptimalTarget.textContent = formatNumber(row.optimalDaily || 0);
  }

  renderWarnings(data, formState) {
    if (!this.hasWarningsTarget) return;
    const root = this.warningsTarget;
    while (root.firstChild) root.removeChild(root.firstChild);

    const banners = [];

    const regulated = (formState.activities || []).some(
      (a) => ["law", "health", "construction", "real_estate"].includes(a.category),
    );
    if (regulated) {
      banners.push({
        level: "warning",
        html:
          "<strong>RC Pro obligatoire.</strong> Au moins une de vos activités est en catégorie réglementée — une responsabilité civile professionnelle est requise avant toute facturation. Prime estimée incluse dans la projection.",
      });
    }

    if (formState.allocation === "are") {
      banners.push({
        level: "warning",
        html:
          "<strong>Estimation ARE indicative.</strong> France Travail applique des règles de plafonnement basées sur le <strong>salaire journalier de référence (SJR)</strong> qui ne sont pas accessibles par API — le montant réellement versé peut différer.",
      });
    }

    const regimes = new Set((formState.activities || []).map((a) => a.regime));
    const isMixed =
      (regimes.has("bic_services") || regimes.has("bic_vente")) && regimes.has("bnc");
    if (isMixed) {
      banners.push({
        level: "info",
        html:
          "<strong>Activité mixte BIC + BNC.</strong> Votre micro-entreprise combine 2 catégories fiscales différentes. Les seuils de franchise TVA et le seuil de chiffre d'affaires micro s'apprécient distinctement par catégorie ; les abattements IR s'appliquent à chaque sous-total (BIC 71 %/50 %, BNC 34 %).",
      });
    }

    (data.warnings || []).forEach((w) => {
      banners.push({
        level: w.level === "error" ? "warning" : w.level || "info",
        html: `<strong>${escapeHtml(w.title || "")}</strong> ${escapeHtml(w.message || "")}`,
      });
    });

    banners.forEach((b) => {
      const fragment = this.cloneTemplate("tpl-warning-banner");
      const banner = fragment.querySelector(".nm-banner");
      if (banner) {
        banner.classList.add(b.level === "info" ? "nm-banner--info" : "nm-banner--warning");
      }
      this.fillHtmlSlots(fragment, { text: b.html });
      root.appendChild(fragment);
    });
  }

  renderGraph(monthlyProjection, formState) {
    if (!this.hasGraphMountTarget) return;
    const months = formState.simulationMonths || 12;
    const caCumul = [0];
    let running = 0;
    const rows = Array.isArray(monthlyProjection) ? monthlyProjection : [];

    if (rows.length > 0) {
      for (let i = 0; i < rows.length && i < months; i += 1) {
        running += Number(rows[i].caHt) || 0;
        caCumul.push(running);
      }
    } else {
      const monthlyTotal = (formState.activities || []).reduce(
        (sum, a) => sum + (Number(a.monthlyCa) || 0),
        0,
      );
      for (let i = 1; i <= months; i += 1) {
        running += monthlyTotal;
        caCumul.push(running);
      }
    }

    const vatThreshold = formState.vatThreshold || 37500;
    const milestones = (rows || [])
      .filter((r) => r.milestoneKey)
      .map((r, idx) => ({
        monthIndex: r.monthIndex ?? idx,
        label: r.milestoneLabel || r.milestoneKey,
        color: r.milestoneColor || null,
      }));

    const ctrl = this.application.getControllerForElementAndIdentifier(
      this.graphMountTarget,
      "svg-graph",
    );
    if (ctrl) {
      ctrl.setData({ months, caCumul, vatThreshold, milestones });
    }
  }
}

function setText(root, selector, value) {
  const el = root.querySelector(selector);
  if (el) el.textContent = value;
}

function pickTotal(totals, key, fallback) {
  if (!totals || typeof totals !== "object") return fallback;
  const v = totals[key];
  return typeof v === "number" ? v : fallback;
}

function formatNumber(value) {
  const n = Number(value) || 0;
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);
}

function formatTimelineDate(date) {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  }
  return date;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
